import Image from 'next/image'
import Link from 'next/link'
import styled, { css, keyframes } from 'styled-components'
import { DISPLAY, MONO } from 'views/TradingCompetitionV3/ui'
import type { Badge } from './logic'

/*
 * The home's own surface tokens. The home is always night (like the hero it replaces), so its text never inherits the light theme's ink.
 * Contrast on NIGHT: SNOW 18.6:1, SNOW_2 13.2:1, MUTED 7.9:1. One hue per tool: Swap violet, Lock ice, Compete amber, Token page blue,
 * Launch is mint-green like the CAD1 tick (Bez 2026-10-10: green and gold, not pink); free is mint too.
 */
export const NIGHT = '#050713'
export const PANEL = '#0b1028'
export const SNOW = '#F4F6FF'
export const SNOW_2 = '#C9D2E6'
export const MUTED = '#93A3BF'
export const ICE = '#7FD4E8'
export const MINT = '#39D98A'
export const LINE = 'rgba(180, 200, 255, 0.18)'
export { DISPLAY, MONO }

export type Hue = 'violet' | 'ice' | 'amber' | 'blue' | 'rose' | 'mint'
/** r, g, b of each hue; the text tint is the light end of the same hue */
export const HUES: Record<Hue, { rgb: string; text: string }> = {
  violet: { rgb: '155, 107, 255', text: '#D7C6FF' },
  ice: { rgb: '127, 212, 232', text: '#BDEFFA' },
  amber: { rgb: '245, 180, 83', text: '#FFD9A0' },
  blue: { rgb: '91, 140, 255', text: '#C3D5FF' },
  rose: { rgb: '255, 122, 182', text: '#FFC2DD' },
  mint: { rgb: '57, 217, 138', text: '#BDF5D8' },
}

const shimmer = keyframes`from { background-position: -160px 0; } to { background-position: 160px 0; }`
export const Skel = styled.span<{ $w?: number | string; $h?: number; $r?: number }>`
  display: inline-block;
  width: ${({ $w = 64 }) => (typeof $w === 'number' ? `${$w}px` : $w)};
  height: ${({ $h = 14 }) => $h}px;
  border-radius: ${({ $r = 8 }) => $r}px;
  background: linear-gradient(90deg, rgba(180, 200, 255, 0.08) 0%, rgba(180, 200, 255, 0.18) 50%, rgba(180, 200, 255, 0.08) 100%);
  background-size: 320px 100%;
  animation: ${shimmer} 1.4s linear infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

/** Decorative 3D icon (Fluent Emoji 3D, MIT; licence in public/images/brand/icons3d/LICENSE): the label next to it carries the meaning. */
export const Icon3d = ({ name, size, className }: { name: string; size: number; className?: string }) => (
  <Image className={className} src={`/images/brand/icons3d/${name}.png`} alt="" width={size} height={size} unoptimized draggable={false} />
)
/** The 72 px Fluent set the rest of the app uses for small heads. */
export const Fluent = ({ name, size }: { name: string; size: number }) => (
  <Image src={`/images/fluent/${name}.png`} alt="" width={size} height={size} unoptimized style={{ flex: 'none' }} />
)

const pill = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  white-space: nowrap;
  font-size: 12px;
  line-height: 16px;
`
const BadgeBox = styled.span<{ $kind: Badge['kind'] }>`
  ${pill}
  ${({ $kind }) =>
    $kind === 'free'
      ? css`
          font-family: ${DISPLAY};
          font-weight: 900;
          font-size: 13px;
          letter-spacing: 0.08em;
          color: #03150c;
          background: linear-gradient(180deg, #6ff0b0 0%, ${MINT} 100%);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55), 0 0 18px rgba(57, 217, 138, 0.45);
        `
      : $kind === 'price'
      ? css`
          font-family: ${MONO};
          font-weight: 700;
          color: ${HUES.amber.text};
          background: rgba(${HUES.amber.rgb}, 0.14);
          border: 1px solid rgba(${HUES.amber.rgb}, 0.55);
        `
      : css`
          font-weight: 700;
          color: ${HUES.ice.text};
          background: rgba(${HUES.ice.rgb}, 0.12);
          border: 1px solid rgba(${HUES.ice.rgb}, 0.42);
        `}
`
/** A tool's live badge. Loading: a pill-sized skeleton; nothing to say: an empty slot of the same height, so the tile never moves. */
export function BadgeView({ b, testId }: { b: Badge; testId?: string }) {
  if (b.kind === 'loading') return <Skel $w={64} $h={26} $r={999} data-testid={testId} data-state="loading" aria-label="Reading the price" />
  if (b.kind === 'none') return <span style={{ display: 'inline-block', height: 26 }} data-testid={testId} data-state="none" />
  return (
    <BadgeBox $kind={b.kind} data-testid={testId} data-state={b.kind}>
      {b.kind === 'free' ? 'FREE' : b.text}
    </BadgeBox>
  )
}

export const Chip = styled.span<{ $hue: Hue }>`
  ${pill}
  height: 24px;
  font-weight: 700;
  color: ${({ $hue }) => HUES[$hue].text};
  background: rgba(${({ $hue }) => HUES[$hue].rgb}, 0.12);
  border: 1px solid rgba(${({ $hue }) => HUES[$hue].rgb}, 0.5);
`

/** The glass panel every board block sits in. */
export const Panel = styled.section<{ $glow?: Hue }>`
  position: relative;
  min-width: 0;
  border-radius: 22px;
  padding: 14px;
  background: ${({ $glow }) => ($glow ? `radial-gradient(70% 45% at 0% 0%, rgba(${HUES[$glow].rgb}, 0.16) 0%, rgba(${HUES[$glow].rgb}, 0) 70%), ` : '')}
    linear-gradient(180deg, rgba(159, 182, 255, 0.07) 0%, rgba(159, 182, 255, 0) 42%), ${PANEL};
  border: 1px solid ${LINE};
  box-shadow: inset 0 1px 0 rgba(223, 233, 255, 0.1), 0 16px 40px rgba(0, 0, 0, 0.4);
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 18px;
  }
`
export const PanelHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  margin-bottom: 8px;
  h2 {
    margin: 0;
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 20px;
    line-height: 26px;
    letter-spacing: -0.01em;
    color: ${SNOW};
  }
  .grow {
    flex: 1;
    min-width: 0;
  }
  .facts {
    display: block;
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`
/** The "See all" link of a panel head: 44 px tall, ice text. */
export const HeadLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 44px;
  padding: 0 4px 0 12px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 14px;
  color: ${ICE};
  white-space: nowrap;
  &:hover {
    color: ${SNOW};
  }
  &:focus-visible {
    outline: 2px solid ${ICE};
    outline-offset: 2px;
  }
`
/** One row of a board list: a whole-row link of a fixed height (the skeleton row has the same height). */
export const RowLink = styled(Link)<{ $h: number }>`
  display: grid;
  align-items: center;
  column-gap: 12px;
  height: ${({ $h }) => $h}px;
  padding: 0 10px;
  border-radius: 14px;
  color: ${SNOW};
  border: 1px solid transparent;
  transition: background 150ms ease, border-color 150ms ease;
  &:hover {
    background: rgba(180, 200, 255, 0.06);
    border-color: ${LINE};
  }
  &:focus-visible {
    outline: 2px solid ${ICE};
    outline-offset: -2px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`
export const Ellipsis = styled.span`
  display: block;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
export const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`
